import { useCallback, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { redirectToLogin } from '@/shell/services/auth'
import { appendErrorBody } from './error-merge'
import { humanizeChatError } from './chat-error'
import { postExecuteConfirmedStream } from '../api/queries'

/** Phase 4 (2026-10): one tool call CARLA made while answering. */
export interface ToolTraceItem {
  id?: string
  name: string
  input?: Record<string, unknown>
  result?: unknown
  ok?: boolean
  truncated?: boolean
  total?: number | null
}

export interface TextMessage {
  role: 'user' | 'assistant'
  content: string
  streaming?: boolean
  error?: boolean
  /** Phase 4 (2026-10): messages.id when known (reloaded history). */
  messageId?: number
  /** Phase 4 (2026-10): tool calls made for this answer ("What CARLA checked"). */
  toolTrace?: ToolTraceItem[]
  /** Set by the `loop_suspected` SSE event (middleware plateau detector).
   *  Indicates CARLA stopped without finishing the turn — the last action
   *  may not have run. UI renders an inline alert under the bubble. */
  loopSuspected?: boolean
  /** Phase 3 (2026-10): response-linter banner — the middleware flagged a
   *  claim this turn's tool calls can't back (e.g. "I verified…" with no
   *  read). Advisory only; the text is untouched. */
  lintBanner?: string
  /** Optional attached image preview for user messages. Data-URL. */
  attachmentPreview?: string
}

/**
 * Destructive (T3) tool confirmation card.
 *
 * Pushed into the transcript when the agent's _validate_confirm_token
 * rejects a T3 dispatch. The user sees the proposed action + a Confirm
 * button. On click, the chat surface POSTs /api/agent/confirm to mint a
 * token, then calls executeConfirmed(tool_name, params, token) which
 * hits the deterministic /api/agent/execute-confirmed endpoint. The
 * backend dispatches the tool directly (no Claude re-emit) and streams
 * a one-turn summary back as SSE.
 *
 * The Sprint 06 staged-action surface (ActionMessage / action_id /
 * `/api/actions/{id}/confirm`) was retired 2026-05-27 — uniformly
 * superseded by this card.
 */
export interface DestructiveConfirmMessage {
  role: 'destructive_confirm'
  tool_name: string
  tool_params: Record<string, unknown>
  /** Human-readable explanation of why this is destructive (from the
   *  agent's denial response — typically includes the tool name + the
   *  reason category like "missing" or "expired"). */
  reason: string
  status: 'pending' | 'confirmed' | 'rejected' | 'expired'
}

export type ChatMessage = TextMessage | DestructiveConfirmMessage

/** Outcome of `executeConfirmed()` — see its docstring below. */
export interface ExecuteConfirmedResult {
  dispatched: boolean
  error?: string
}

export interface Attachment {
  /** Base64-encoded bytes (no data-URL prefix). */
  base64: string
  /** e.g. "image/png", "image/jpeg", or the xlsx MIME type. */
  mediaType: string
  /** For image previews — data-URL including prefix. Empty string for
   *  non-image attachments (xlsx shows a filename chip instead). */
  previewDataUrl: string
  /** Original filename. Surfaced to CARLA for xlsx attachments so she can
   *  reference the sheet by name; used for the preview chip display. */
  filename?: string
}

type StreamEvent =
  | { event: 'text_delta'; data: { text: string } }
  | {
      event: 'tool_use'
      data: { id?: string; name: string; input?: Record<string, unknown> }
    }
  | { event: 'tool_start'; data: { name: string } }
  | {
      event: 'tool_progress'
      data: { name: string; done: number; total: number; last_key?: string | null }
    }
  | {
      event: 'tool_result'
      data: {
        id?: string
        name?: string
        result: {
          // Server-side T3 gate (Sprint 25). When set, the frontend
          // renders the DestructiveConfirmMessage card so the user can
          // mint a token via /api/agent/confirm and proceed via the
          // deterministic /api/agent/execute-confirmed endpoint
          // (2026-05-27).
          confirm_token_required?: boolean
          confirm_token_result?: string
          error?: string
        }
      }
    }
  | { event: 'thinking'; data?: unknown }
  // Phase 1 (2026-10): adaptive-thinking progress summaries from the model.
  | { event: 'thinking_summary'; data: { text: string } }
  | { event: 'done'; data?: { usage?: Record<string, number>; model?: string } }
  | { event: 'loop_suspected'; data?: { iteration?: number } }
  | { event: 'lint'; data: { flags: { code: string; detail: string }[]; banner?: string | null } }
  | { event: 'error'; data: { message: string } }

/**
 * CARLA chat hook — SSE streaming against POST /api/chat/stream.
 *
 * Ported from the archived middleware frontend. Key changes for ops:
 *   - TypeScript types
 *   - 401 redirects via redirectToLogin() (preserves the intended path
 *     via sessionStorage), not the deprecated open-orders alias
 *   - `sendMessage(text, attachments?)` accepts a list of attachments
 *     (Sprint 19 hot-fix 2026-05-05 — bulk PDF support).
 *
 * The middleware contract:
 *   Request body:
 *     { message, conversation_id?, attachments?: [{ data, media_type, filename }] }
 *   Legacy single-slot fields (image_data, image_media_type, attachment_filename)
 *   are still accepted server-side for older clients; the new `attachments`
 *   array is the canonical path and wins when both are sent.
 *   SSE events: text_delta · tool_use · tool_result · thinking · done · error
 *   Response header: X-Conversation-ID (new conversations get their id here)
 */
export interface UseCarlaChatOptions {
  /**
   * Returns the `odoo_context` object to attach to each chat send.
   * Called fresh on every `sendMessage` so callers can include
   * up-to-date state (e.g. current intake fields). The middleware
   * stringifies this and injects it into the agent's system prompt.
   * Return `null`/`undefined` to omit.
   */
  getOdooContext?: () => Record<string, unknown> | null | undefined
}

export function useCarlaChat(
  initialConversationId: string | null = null,
  options: UseCarlaChatOptions = {},
) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [activeTool, setActiveTool] = useState<string | null>(null)
  // Phase 1 (2026-10): last thinking summary text (shown under the indicator).
  const [thinkingNote, setThinkingNote] = useState<string>('')
  const [toolProgress, setToolProgress] = useState<{
    name: string
    done: number
    total: number
    lastKey?: string | null
  } | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // True when the user clicked Stop. AbortError with this flag == intentional.
  // AbortError WITHOUT this flag == network dropped / proxy timed out —
  // surface an explicit message so the user knows data may still be
  // landing server-side.
  const userAbortedRef = useRef(false)
  // Sprint 18 Chunk 6: `admin` no longer rides in the chat request body.
  // The middleware derives admin entirely from the authenticated session's
  // RBAC role (Sprint 06 follow-on, 2026-04-23) and as of Sprint 18 the
  // entire admin-module mechanism was retired in favor of tier-based
  // gating (`tier_assignments.py`). Frontend-asserted admin was always a
  // forgeable signal; dropping it removes the misleading client-side
  // ceremony.

  const hydrateFromHistory = useCallback((history: ChatMessage[], id: string | null) => {
    setMessages(history)
    setConversationId(id)
    setIsStreaming(false)
    setActiveTool(null)
    setThinkingNote('')
  }, [])

  const newConversation = useCallback(() => {
    setMessages([])
    setConversationId(null)
    setIsStreaming(false)
    setActiveTool(null)
    setToolProgress(null)
    setThinkingNote('')
  }, [])

  const stopStreaming = useCallback(() => {
    userAbortedRef.current = true
    setIsStopping(true)
    abortRef.current?.abort()
  }, [])

  const updateMessageAt = useCallback((index: number, patch: Partial<ChatMessage>) => {
    setMessages((prev) => {
      const updated = prev.slice()
      const existing = updated[index]
      if (!existing) return prev
      updated[index] = { ...existing, ...patch } as ChatMessage
      return updated
    })
  }, [])

  const sendMessage = useCallback(
    async (text: string, attachments?: Attachment[], opts?: { deepDive?: boolean }) => {
      const atts = attachments ?? []
      if ((!text.trim() && atts.length === 0) || isStreaming) return

      // Show first image preview in the user bubble (multi-attachment
      // turns just show one — full list is in the middleware).
      const firstImage = atts.find((a) => a.previewDataUrl)
      const userMsg: TextMessage = {
        role: 'user',
        content: text,
        attachmentPreview: firstImage?.previewDataUrl,
      }
      setMessages((prev) => [...prev, userMsg])
      setIsStreaming(true)
      setActiveTool(null)
      setToolProgress(null)
      setThinkingNote('')
      userAbortedRef.current = false

      let assistantText = ''
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', streaming: true } satisfies TextMessage,
      ])

      // Sprint 29 — track tool_use (id → input) so we can correlate
      // when a tool_result comes back with confirm_token_required and
      // surface the original params on the DestructiveConfirmMessage.
      const toolUsesById = new Map<string, { name: string; input: Record<string, unknown> }>()

      try {
        const controller = new AbortController()
        abortRef.current = controller

        const body: Record<string, unknown> = {
          message: text,
          conversation_id: conversationId,
        }
        // Phase 1 (2026-10): deep dive = higher effort (and the deep-dive
        // model when configured) for diagnostic / root-cause turns.
        if (opts?.deepDive) body.deep_dive = true
        const ctx = options.getOdooContext?.()
        if (ctx && Object.keys(ctx).length > 0) {
          body.odoo_context = ctx
        }
        if (atts.length > 0) {
          // Sprint 19 hot-fix: send the new `attachments` array.
          // Server promotes the legacy `image_data` slot when this is
          // empty, so older middleware deploys still work.
          body.attachments = atts.map((a) => ({
            data: a.base64,
            media_type: a.mediaType,
            ...(a.filename ? { filename: a.filename } : {}),
          }))
        }
        const response = await fetch('/api/chat/stream', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        })

        if (response.status === 401) {
          redirectToLogin()
          return
        }
        if (!response.ok) {
          // Read FastAPI's body so the inline error names the real reason
          // (e.g. an over-length message) instead of a dead-end "HTTP 422".
          const errBody = await response.json().catch(() => null)
          throw new Error(humanizeChatError(response.status, errBody))
        }

        const convId = response.headers.get('x-conversation-id')
        if (convId) setConversationId(convId)

        const reader = response.body?.getReader()
        if (!reader) throw new Error('Response body is not readable')
        const decoder = new TextDecoder()
        let buffer = ''

         
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const jsonStr = line.slice(6).trim()
            if (!jsonStr) continue

            let event: StreamEvent
            try {
              event = JSON.parse(jsonStr) as StreamEvent
            } catch {
              continue
            }

            switch (event.event) {
              case 'text_delta':
                assistantText += event.data?.text ?? ''
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: assistantText }
                  }
                  return updated
                })
                break

              case 'tool_use':
                setActiveTool(event.data?.name ?? 'tool')
                setToolProgress(null)
                // Sprint 29 — record (id → name+input) so we can render
                // the DestructiveConfirmCard with the original params if
                // this tool dispatch comes back with confirm_token_required.
                if (event.data?.id && event.data?.name) {
                  toolUsesById.set(event.data.id, {
                    name: event.data.name,
                    input: event.data.input ?? {},
                  })
                }
                // Phase 4 (2026-10): accumulate the trace on the in-flight answer.
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    const trace = [...(last.toolTrace ?? []), {
                      id: event.data?.id,
                      name: event.data?.name ?? 'tool',
                      input: event.data?.input ?? {},
                    }]
                    updated[updated.length - 1] = { ...last, toolTrace: trace }
                  }
                  return updated
                })
                break

              case 'tool_start':
                setActiveTool(event.data?.name ?? 'tool')
                setToolProgress(null)
                break

              case 'tool_progress': {
                const d = event.data
                if (d && typeof d.done === 'number' && typeof d.total === 'number') {
                  setToolProgress({
                    name: d.name,
                    done: d.done,
                    total: d.total,
                    lastKey: d.last_key ?? null,
                  })
                  setActiveTool(d.name)
                }
                break
              }

              case 'tool_result': {
                // Don't clear to null — Claude is almost always about to
                // fire the next tool_use, and a null gap between
                // tool_result and the next tool_use makes the UI look
                // like CARLA quit. Fall back to the generic 'thinking'
                // indicator so the activity state stays visible until
                // the 'done' event actually ends the turn.
                setActiveTool('thinking')
                setToolProgress(null)
                const r = event.data?.result
                // Phase 4 (2026-10): attach the result to its trace item.
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant' && last.toolTrace) {
                    const res = r as Record<string, unknown> | undefined
                    const trace = last.toolTrace.map((t) =>
                      t.id && t.id === event.data?.id
                        ? {
                            ...t,
                            result: r,
                            ok: !(res && (res.error || res.ok === false)),
                            truncated: Boolean(res && (res.truncated || res._truncated)),
                            total: typeof res?.total === 'number' ? (res.total as number) : null,
                          }
                        : t,
                    )
                    updated[updated.length - 1] = { ...last, toolTrace: trace }
                  }
                  return updated
                })
                // Server-side T3 confirm gate denial. Look up the
                // original tool_use by id, capture its (name, input),
                // and push a DestructiveConfirmMessage card. The user
                // clicks Confirm → mint token → executeConfirmed runs
                // the action deterministically via the new endpoint.
                if (r?.confirm_token_required) {
                  const id = event.data?.id
                  const recorded = id ? toolUsesById.get(id) : undefined
                  const toolName = recorded?.name ?? event.data?.name ?? 'unknown_tool'
                  const toolParams = recorded?.input ?? {}
                  const reason = r.error ?? `${toolName} requires explicit confirmation.`
                  setMessages((prev) => [
                    ...prev,
                    {
                      role: 'destructive_confirm',
                      tool_name: toolName,
                      tool_params: toolParams,
                      reason,
                      status: 'pending',
                    } satisfies DestructiveConfirmMessage,
                  ])
                }
                break
              }

              case 'thinking':
                setActiveTool('thinking')
                break

              case 'thinking_summary':
                setActiveTool((cur) => cur ?? 'thinking')
                setThinkingNote((prev) => (prev + ' ' + (event.data?.text ?? '')).trim().slice(-280))
                break

              case 'done':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, streaming: false }
                  }
                  return updated
                })
                break

              case 'loop_suspected':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, loopSuspected: true }
                  }
                  return updated
                })
                break

              case 'lint':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant' && event.data?.banner) {
                    updated[updated.length - 1] = { ...last, lintBanner: event.data.banner }
                  }
                  return updated
                })
                break

              case 'error': {
                // Sprint Z Chunk 2 — preserve accumulated text and
                // append the error as a markdown-italics trailer
                // instead of overwriting. Lost-text bug fixed.
                const errorBody = `Error: ${event.data?.message ?? 'Something went wrong'}`
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      content: appendErrorBody(last.content, errorBody),
                      streaming: false,
                      error: true,
                    }
                  }
                  return updated
                })
                break
              }
            }
          }
        }
      } catch (err) {
        const errorName = (err as Error).name
        // User-initiated abort: silent (they know they stopped it).
        // Anything else (including AbortError from a dropped network or
        // proxy timeout mid-stream) gets a clear inline message so the
        // user knows the stream ended unexpectedly and backend work may
        // still be landing.
        if (errorName === 'AbortError' && userAbortedRef.current) {
          // intentional stop — no message
        } else {
          const isNetworkAbort = errorName === 'AbortError'
          if (!isNetworkAbort) {
            // Genuine failures (validation 422, 5xx, parse errors) used to
            // be swallowed entirely — invisible to Sentry. Surface them now.
            // Network drops mid-stream stay quiet: expected + self-recovering.
            Sentry.captureException(err)
          }
          const body = isNetworkAbort
            ? 'Connection dropped mid-response. Any work CARLA started may still be completing server-side — reopen the conversation in a moment to see the final result.'
            : `Error: ${(err as Error).message}`
          setMessages((prev) => {
            const updated = prev.slice()
            const last = updated[updated.length - 1]
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                content: last.content
                  ? `${last.content}\n\n_${body}_`
                  : body,
                streaming: false,
                error: true,
              }
            }
            return updated
          })
        }
      } finally {
        setIsStreaming(false)
        setIsStopping(false)
        setActiveTool(null)
        setToolProgress(null)
        abortRef.current = null
        userAbortedRef.current = false
      }
    },
    [conversationId, isStreaming, options],
  )

  /**
   * Deterministic confirm-execute (2026-05-27).
   *
   * Wired into DestructiveConfirmCard.onConfirm. Mints a confirm_token
   * (via the card's own POST /api/agent/confirm), then calls this with
   * the (tool_name, params, token). POSTs to /api/agent/execute-confirmed
   * — the backend dispatches the tool directly + streams a one-turn
   * summary. SSE event shape is identical to /api/chat/stream so the
   * same handlers below work.
   *
   * Distinct from sendMessage: no user bubble is added (the user clicked
   * a button, didn't type), no request body field beyond the
   * (conv, tool, params, token) quadruple.
   *
   * Returns `{dispatched}` so the card knows whether to flip to
   * "Confirmed" — `dispatched: false` means nothing ran server-side,
   * with `error` carrying the specific reason (token rejected, network
   * failure, etc.) so the card can show it instead of a generic message.
   */
  const executeConfirmed = useCallback(
    async (
      toolName: string,
      toolParams: Record<string, unknown>,
      confirmToken: string,
    ): Promise<ExecuteConfirmedResult> => {
      if (!conversationId) {
        return {
          dispatched: false,
          error: 'This conversation has not been saved yet — reload the page and confirm again. Nothing changed.',
        }
      }
      if (isStreaming) {
        return {
          dispatched: false,
          error: 'CARLA is still replying — confirm again once she finishes. Nothing changed.',
        }
      }

      setIsStreaming(true)
      setActiveTool(null)
      setToolProgress(null)
      setThinkingNote('')
      userAbortedRef.current = false

      let assistantText = ''
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', streaming: true } satisfies TextMessage,
      ])

      const toolUsesById = new Map<string, { name: string; input: Record<string, unknown> }>()

      // Set once the HTTP response comes back `ok`. Per agent_execute.py,
      // the backend dispatches the tool and persists the synthetic
      // tool_use/tool_result exchange synchronously inside the route
      // handler *before* it returns the StreamingResponse — so `ok` here
      // means the action has already happened server-side, regardless of
      // what happens to the SSE summary stream afterward. Only a non-ok
      // response or a network failure before that point means the action
      // never dispatched.
      let dispatched = false
      // Only meaningful when dispatched stays false — the specific reason
      // the backend rejected the dispatch (e.g. token expired/consumed/
      // params-mismatch), so the card can show it instead of a generic
      // "not executed" message.
      let dispatchError: string | undefined

      try {
        const controller = new AbortController()
        abortRef.current = controller

        const response = await postExecuteConfirmedStream(
          conversationId,
          toolName,
          toolParams,
          confirmToken,
          controller.signal,
        )

        if (response.status === 401) {
          redirectToLogin()
          return { dispatched: false }
        }
        if (!response.ok) {
          const errBody = await response.json().catch(() => null)
          throw new Error(humanizeChatError(response.status, errBody))
        }

        dispatched = true

        const reader = response.body?.getReader()
        if (!reader) throw new Error('Response body is not readable')
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const jsonStr = line.slice(6).trim()
            if (!jsonStr) continue

            let event: StreamEvent
            try {
              event = JSON.parse(jsonStr) as StreamEvent
            } catch {
              continue
            }

            switch (event.event) {
              case 'text_delta':
                assistantText += event.data?.text ?? ''
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: assistantText }
                  }
                  return updated
                })
                break

              case 'tool_use':
                setActiveTool(event.data?.name ?? 'tool')
                setToolProgress(null)
                if (event.data?.id && event.data?.name) {
                  toolUsesById.set(event.data.id, {
                    name: event.data.name,
                    input: event.data.input ?? {},
                  })
                }
                // Phase 4 (2026-10): accumulate the trace on the in-flight answer.
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    const trace = [...(last.toolTrace ?? []), {
                      id: event.data?.id,
                      name: event.data?.name ?? 'tool',
                      input: event.data?.input ?? {},
                    }]
                    updated[updated.length - 1] = { ...last, toolTrace: trace }
                  }
                  return updated
                })
                break

              case 'tool_start':
                setActiveTool(event.data?.name ?? 'tool')
                setToolProgress(null)
                break

              case 'tool_progress': {
                const d = event.data
                if (d && typeof d.done === 'number' && typeof d.total === 'number') {
                  setToolProgress({
                    name: d.name,
                    done: d.done,
                    total: d.total,
                    lastKey: d.last_key ?? null,
                  })
                  setActiveTool(d.name)
                }
                break
              }

              case 'tool_result': {
                setActiveTool('thinking')
                setToolProgress(null)
                const r = event.data?.result
                // Phase 4 (2026-10): attach the result to its trace item.
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant' && last.toolTrace) {
                    const res = r as Record<string, unknown> | undefined
                    const trace = last.toolTrace.map((t) =>
                      t.id && t.id === event.data?.id
                        ? {
                            ...t,
                            result: r,
                            ok: !(res && (res.error || res.ok === false)),
                            truncated: Boolean(res && (res.truncated || res._truncated)),
                            total: typeof res?.total === 'number' ? (res.total as number) : null,
                          }
                        : t,
                    )
                    updated[updated.length - 1] = { ...last, toolTrace: trace }
                  }
                  return updated
                })
                if (r?.confirm_token_required) {
                  const id = event.data?.id
                  const recorded = id ? toolUsesById.get(id) : undefined
                  const name = recorded?.name ?? event.data?.name ?? 'unknown_tool'
                  const input = recorded?.input ?? {}
                  const reason = r.error ?? `${name} requires explicit confirmation.`
                  setMessages((prev) => [
                    ...prev,
                    {
                      role: 'destructive_confirm',
                      tool_name: name,
                      tool_params: input,
                      reason,
                      status: 'pending',
                    } satisfies DestructiveConfirmMessage,
                  ])
                }
                break
              }

              case 'thinking':
                setActiveTool('thinking')
                break

              case 'thinking_summary':
                setActiveTool((cur) => cur ?? 'thinking')
                setThinkingNote((prev) => (prev + ' ' + (event.data?.text ?? '')).trim().slice(-280))
                break

              case 'done':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, streaming: false }
                  }
                  return updated
                })
                break

              case 'loop_suspected':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, loopSuspected: true }
                  }
                  return updated
                })
                break

              case 'lint':
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant' && event.data?.banner) {
                    updated[updated.length - 1] = { ...last, lintBanner: event.data.banner }
                  }
                  return updated
                })
                break

              case 'error': {
                const errorBody = `Error: ${event.data?.message ?? 'Something went wrong'}`
                setMessages((prev) => {
                  const updated = prev.slice()
                  const last = updated[updated.length - 1]
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      content: appendErrorBody(last.content, errorBody),
                      streaming: false,
                      error: true,
                    }
                  }
                  return updated
                })
                break
              }
            }
          }
        }
      } catch (err) {
        const errorName = (err as Error).name
        if (errorName === 'AbortError' && userAbortedRef.current) {
          // intentional stop — no message
        } else {
          const isNetworkAbort = errorName === 'AbortError'
          if (!isNetworkAbort) {
            Sentry.captureException(err)
          }
          const body = isNetworkAbort
            ? 'Connection dropped mid-response. The confirmed action may still be completing server-side — reopen the conversation in a moment to see the final result.'
            : `Error: ${(err as Error).message}`
          if (!dispatched) {
            dispatchError = isNetworkAbort ? body : (err as Error).message
          }
          setMessages((prev) => {
            const updated = prev.slice()
            const last = updated[updated.length - 1]
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                content: last.content ? `${last.content}\n\n_${body}_` : body,
                streaming: false,
                error: true,
              }
            }
            return updated
          })
        }
      } finally {
        setIsStreaming(false)
        setIsStopping(false)
        setActiveTool(null)
        setToolProgress(null)
        abortRef.current = null
        userAbortedRef.current = false
      }

      return { dispatched, error: dispatchError }
    },
    [conversationId, isStreaming],
  )

  return {
    messages,
    conversationId,
    isStreaming,
    isStopping,
    activeTool,
    toolProgress,
    thinkingNote,
    sendMessage,
    executeConfirmed,
    stopStreaming,
    newConversation,
    updateMessageAt,
    hydrateFromHistory,
  }
}
